import { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";

import workspaceService from "api/workspaceService";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Spinner from "components/Spinner";
import {
  ITransferUserWorkItemsRequest,
  ITransferUserWorkItemsResponse,
  IUserWorkItemsJrwaCode,
  IUserWorkItemsReport,
} from "dto/Workspace/IUserWorkItems";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

// Transfer danych (zawartości) członka zespołu — port UserWorkItemsTransferWizardWindow
// z Magistratu. Okno ładuje raport z BE (liczniki per grupa + możliwi odbiorcy) i pozwala
// zaznaczyć grupy z odbiorcą każdej z nich — jak w Magistracie. Rozszerzenie: grupa „Teczki"
// rozwija się w kody JRWA użyte przez użytkownika; kody zaznacza się osobno i każdy może
// mieć własnego odbiorcę (domyślnie odbiorca grupy). BE rozwiązuje zbiory w chwili
// wykonania i zapisuje audyt (UserWorkItemsTransfers).

type GroupKey = "budget" | "parcels" | "approvals" | "tasks";

interface GroupState {
  checked: boolean;
  target: string;
}

interface CodeState {
  checked: boolean;
  /** "" = odbiorca grupy. */
  target: string;
}

const NO_CODE_KEY = "__none__";

const codeKey = (code: IUserWorkItemsJrwaCode) =>
  code.ClassificationId ?? NO_CODE_KEY;

const emptyGroup = (): GroupState => ({ checked: false, target: "" });

const UserWorkItemsTransferModal: FC<Props> = ({ member, toggle, refetch }) => {
  const { t } = useTranslation();

  const {
    data: reportData,
    isLoading,
    error: reportError,
  } = workspaceService.useGetUserWorkItemsReport(member.UserId);
  const report: IUserWorkItemsReport | undefined = reportData?.data;

  const { mutate: transfer, isLoading: isTransferring } =
    workspaceService.useTransferUserWorkItems();

  // ── Stan zaznaczeń ─────────────────────────────────────────────────────────
  const [cases, setCases] = useState<GroupState>(emptyGroup());
  const [codes, setCodes] = useState<Record<string, CodeState>>({});
  const [expanded, setExpanded] = useState(false);
  const [includeShared, setIncludeShared] = useState(false);
  const [includeDocuments, setIncludeDocuments] = useState(false);
  const [groups, setGroups] = useState<Record<GroupKey, GroupState>>({
    budget: emptyGroup(),
    parcels: emptyGroup(),
    approvals: emptyGroup(),
    tasks: emptyGroup(),
  });

  useEffect(() => {
    if (!report) return;
    const initial: Record<string, CodeState> = {};
    report.Cases.Codes.forEach((code) => {
      initial[codeKey(code)] = { checked: false, target: "" };
    });
    setCodes(initial);
  }, [report]);

  const targetUsers = useMemo(() => report?.TargetUsers ?? [], [report]);

  const casesHasItems =
    !!report &&
    (report.Cases.CasesCount > 0 ||
      report.Cases.SharedCasesCount > 0 ||
      report.Cases.DocumentsCount > 0);

  const groupHasItems: Record<GroupKey, boolean> = {
    budget: !!report?.Budget.HasItems,
    parcels: !!report?.Parcels.HasItems,
    approvals: (report?.Approvals.PendingCount ?? 0) > 0,
    tasks: (report?.Tasks.TasksCount ?? 0) > 0,
  };

  // Zaznaczenie grupy „Teczki" zaznacza wszystko w środku (kody, udostępnienia, pisma);
  // odznaczenie — czyści. Kody można potem odznaczać pojedynczo.
  const toggleCasesGroup = (checked: boolean) => {
    setCases((prev) => ({ ...prev, checked }));
    setCodes((prev) => {
      const next: Record<string, CodeState> = {};
      Object.keys(prev).forEach((key) => {
        next[key] = { ...prev[key], checked };
      });
      return next;
    });
    setIncludeShared(checked && (report?.Cases.SharedCasesCount ?? 0) > 0);
    setIncludeDocuments(checked && (report?.Cases.DocumentsCount ?? 0) > 0);
    if (checked) setExpanded(true);
  };

  const anyCaseItemChecked =
    Object.values(codes).some((c) => c.checked) ||
    includeShared ||
    includeDocuments;

  const toggleCode = (key: string, checked: boolean) => {
    setCodes((prev) => ({ ...prev, [key]: { ...prev[key], checked } }));
    if (checked) setCases((prev) => ({ ...prev, checked: true }));
  };

  const setCodeTarget = (key: string, target: string) => {
    setCodes((prev) => ({ ...prev, [key]: { ...prev[key], target } }));
  };

  const toggleGroup = (key: GroupKey, checked: boolean) => {
    setGroups((prev) => ({ ...prev, [key]: { ...prev[key], checked } }));
  };

  const setGroupTarget = (key: GroupKey, target: string) => {
    setGroups((prev) => ({ ...prev, [key]: { ...prev[key], target } }));
  };

  // ── Walidacja (Magistrat: OnPreviewApply) ──────────────────────────────────
  const validationError = (): string | null => {
    const casesActive = cases.checked && anyCaseItemChecked;
    const activeGroups = (Object.keys(groups) as GroupKey[]).filter(
      (key) => groups[key].checked,
    );

    if (!casesActive && activeGroups.length === 0) {
      return t("workspace.transfer.nothingSelected");
    }

    if (casesActive) {
      const needsDefault =
        includeShared ||
        includeDocuments ||
        Object.values(codes).some((c) => c.checked && !c.target);
      if (needsDefault && !cases.target) {
        return t("workspace.transfer.missingReceiver");
      }
    }

    if (activeGroups.some((key) => !groups[key].target)) {
      return t("workspace.transfer.missingReceiver");
    }

    return null;
  };

  const buildRequest = (): ITransferUserWorkItemsRequest => {
    const request: ITransferUserWorkItemsRequest = {
      SourceUserId: member.UserId,
    };

    if (cases.checked && anyCaseItemChecked) {
      request.Cases = {
        TargetUserId: cases.target || null,
        Codes: (report?.Cases.Codes ?? [])
          .filter((code) => codes[codeKey(code)]?.checked)
          .map((code) => ({
            ClassificationId: code.ClassificationId,
            TargetUserId: codes[codeKey(code)]?.target || null,
          })),
        IncludeSharedCases: includeShared,
        IncludeDocuments: includeDocuments,
      };
    }

    (Object.keys(groups) as GroupKey[]).forEach((key) => {
      if (groups[key].checked) {
        const payload = { TargetUserId: groups[key].target };
        if (key === "budget") request.Budget = payload;
        if (key === "parcels") request.Parcels = payload;
        if (key === "approvals") request.Approvals = payload;
        if (key === "tasks") request.Tasks = payload;
      }
    });

    return request;
  };

  const summaryOf = (r: ITransferUserWorkItemsResponse) =>
    (
      [
        ["cases", r.CasesCount],
        ["sharedCases", r.SharedCasesCount],
        ["documents", r.DocumentsCount],
        ["expenses", r.ExpensesCount],
        ["sharedExpenses", r.SharedExpensesCount],
        ["invoices", r.InvoicesCount],
        ["engagements", r.EngagementsCount],
        ["parcels", r.ParcelsCount],
        ["decrees", r.DecreesCount],
        ["approvals", r.ApprovalsCount],
        ["tasks", r.TasksCount],
      ] as [string, number][]
    )
      .filter(([, count]) => count > 0)
      .map(([key, count]) => `${t(`workspace.transfer.summary.${key}`)} ${count}`)
      .join(", ");

  const handleTransfer = () => {
    const problem = validationError();
    if (problem) {
      notification({
        type: ENotificationType.ERROR,
        title: t("workspace.transfer.title"),
        text: problem,
      });
      return;
    }

    transfer(buildRequest(), {
      onSuccess: (response) => {
        const result = response?.data;
        const total = result?.TotalCount ?? 0;
        notification({
          type: total > 0 ? ENotificationType.SUCCESS : ENotificationType.INFO,
          title: t("workspace.transfer.successTitle"),
          text:
            total > 0 && result
              ? t("workspace.transfer.successText", {
                  name: report?.UserLabel ?? member.Label,
                  summary: summaryOf(result),
                })
              : t("workspace.transfer.nothingText"),
        });
        refetch?.();
        toggle();
      },
      onError: (error) => {
        notification({
          type: ENotificationType.ERROR,
          title: t("workspace.transfer.title"),
          text: getErrorMessage(error) || t("workspace.transfer.errorText"),
        });
      },
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const renderTargetSelect = (
    value: string,
    onChange: (value: string) => void,
    disabled: boolean,
    options?: { inherit?: boolean; small?: boolean },
  ) => (
    <FormControl
      size="small"
      className={options?.small ? styles.selectSmall : styles.select}
      disabled={disabled}
    >
      <InputLabel>{t("workspace.transfer.passTo")}</InputLabel>
      <Select
        label={t("workspace.transfer.passTo")}
        value={value}
        onChange={(e) => onChange(e.target.value as string)}
        MenuProps={{ sx: { zIndex: 10000000 } }}
      >
        {options?.inherit ? (
          <MenuItem value="">
            <em>{t("workspace.transfer.asGroup")}</em>
          </MenuItem>
        ) : (
          <MenuItem value="" disabled>
            <em>{t("workspace.transfer.chooseReceiver")}</em>
          </MenuItem>
        )}
        {targetUsers.map((user) => (
          <MenuItem key={user.UserId} value={user.UserId}>
            {user.Label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderGroupRow = (
    key: GroupKey,
    label: string,
    count: number,
    details?: string,
  ) => {
    const enabled = groupHasItems[key];
    return (
      <div className={`${styles.row} ${enabled ? "" : styles.rowDisabled}`}>
        <div className={styles.rowMain}>
          <Checkbox
            size="small"
            checked={groups[key].checked}
            disabled={!enabled}
            onChange={(e) => toggleGroup(key, e.target.checked)}
          />
          <div className={styles.rowLabel}>
            <span className={styles.rowName}>
              {label} <span className={styles.count}>({count})</span>
            </span>
            {details && <span className={styles.rowDetails}>{details}</span>}
          </div>
        </div>
        {renderTargetSelect(
          groups[key].target,
          (value) => setGroupTarget(key, value),
          !groups[key].checked,
        )}
      </div>
    );
  };

  const renderCodeRow = (code: IUserWorkItemsJrwaCode) => {
    const key = codeKey(code);
    const state = codes[key] ?? { checked: false, target: "" };
    const label =
      code.ClassificationId === null
        ? t("workspace.transfer.groups.noCode")
        : `${code.Code ?? ""} ${code.Description ?? ""}`.trim();

    return (
      <div key={key} className={styles.codeRow}>
        <div className={styles.rowMain}>
          <Checkbox
            size="small"
            checked={state.checked}
            onChange={(e) => toggleCode(key, e.target.checked)}
          />
          <div className={styles.rowLabel}>
            <span className={styles.codeName}>
              {code.ClassificationId !== null && (
                <span className={styles.code}>{code.Code}</span>
              )}
              <span className={styles.codeDescription}>
                {code.ClassificationId === null ? label : code.Description}
              </span>
              <span className={styles.count}>({code.CasesCount})</span>
            </span>
            {(code.IsShared || code.IsLeadingUser) && (
              <span className={styles.rowDetails}>
                {[
                  code.IsShared ? t("workspace.transfer.sharedCode") : null,
                  code.IsLeadingUser
                    ? t("workspace.transfer.leadingUser")
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            )}
          </div>
        </div>
        {renderTargetSelect(
          state.target,
          (value) => setCodeTarget(key, value),
          !state.checked,
          { inherit: true, small: true },
        )}
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
      <div className={styles.wrapper}>
        <div className={styles.title}>{t("workspace.transfer.title")}</div>
        <div className={styles.subtitle}>
          {t("workspace.transfer.subtitle", {
            name: report?.UserLabel ?? member.Label,
          })}
        </div>
        <div className={styles.description}>
          {t("workspace.transfer.description")}
        </div>

        {isLoading && (
          <div className={styles.loader}>
            <Spinner />
          </div>
        )}

        {!isLoading && reportError && (
          <div className={styles.error}>
            {getErrorMessage(reportError) || t("errors.somethingWentWrong")}
          </div>
        )}

        {!isLoading && report && !report.HasItems && (
          <div className={styles.empty}>{t("workspace.transfer.empty")}</div>
        )}

        {!isLoading && report && report.HasItems && (
          <div className={styles.list}>
            {/* Teczki — grupa rozwijalna w kody JRWA */}
            <div
              className={`${styles.row} ${casesHasItems ? "" : styles.rowDisabled}`}
            >
              <div className={styles.rowMain}>
                <Checkbox
                  size="small"
                  checked={cases.checked}
                  indeterminate={
                    cases.checked &&
                    anyCaseItemChecked &&
                    (Object.values(codes).some((c) => !c.checked) ||
                      (!includeShared && report.Cases.SharedCasesCount > 0) ||
                      (!includeDocuments && report.Cases.DocumentsCount > 0))
                  }
                  disabled={!casesHasItems}
                  onChange={(e) => toggleCasesGroup(e.target.checked)}
                />
                <div className={styles.rowLabel}>
                  <span className={styles.rowName}>
                    {t("workspace.transfer.groups.cases")}{" "}
                    <span className={styles.count}>
                      ({report.Cases.CasesCount})
                    </span>
                  </span>
                  {report.Cases.Codes.length > 0 && (
                    <button
                      type="button"
                      className={styles.expand}
                      onClick={() => setExpanded((v) => !v)}
                    >
                      {expanded
                        ? t("workspace.transfer.collapse")
                        : t("workspace.transfer.expand")}
                    </button>
                  )}
                </div>
              </div>
              {renderTargetSelect(
                cases.target,
                (value) => setCases((prev) => ({ ...prev, target: value })),
                !cases.checked,
              )}
            </div>

            {expanded && report.Cases.Codes.length > 0 && (
              <div className={styles.codes}>
                <div className={styles.codesTitle}>
                  {t("workspace.transfer.groups.casesCodes")}
                </div>
                {report.Cases.Codes.map(renderCodeRow)}
              </div>
            )}

            {/* Teczki udostępnione + pisma — części grupy „Teczki" (odbiorca grupy) */}
            <div className={styles.subRows}>
                <div
                  className={`${styles.subRow} ${report.Cases.SharedCasesCount === 0 ? styles.rowDisabled : ""}`}
                >
                  <Checkbox
                    size="small"
                    checked={includeShared}
                    disabled={report.Cases.SharedCasesCount === 0}
                    onChange={(e) => {
                      setIncludeShared(e.target.checked);
                      if (e.target.checked)
                        setCases((prev) => ({ ...prev, checked: true }));
                    }}
                  />
                  <span className={styles.rowName}>
                    {t("workspace.transfer.groups.sharedCases")}{" "}
                    <span className={styles.count}>
                      ({report.Cases.SharedCasesCount})
                    </span>
                  </span>
                </div>
              <div
                className={`${styles.subRow} ${report.Cases.DocumentsCount === 0 ? styles.rowDisabled : ""}`}
              >
                <Checkbox
                  size="small"
                  checked={includeDocuments}
                  disabled={report.Cases.DocumentsCount === 0}
                  onChange={(e) => {
                    setIncludeDocuments(e.target.checked);
                    if (e.target.checked)
                      setCases((prev) => ({ ...prev, checked: true }));
                  }}
                />
                <span className={styles.rowName}>
                  {t("workspace.transfer.groups.documents")}{" "}
                  <span className={styles.count}>
                    ({report.Cases.DocumentsCount})
                  </span>
                </span>
              </div>
            </div>

            {renderGroupRow(
              "budget",
              t("workspace.transfer.groups.budget"),
              report.Budget.ExpensesCount +
                report.Budget.SharedExpensesCount +
                report.Budget.InvoicesCount +
                report.Budget.EngagementsCount,
              t("workspace.transfer.groups.budgetDetails", {
                expenses: report.Budget.ExpensesCount,
                shared: report.Budget.SharedExpensesCount,
                invoices: report.Budget.InvoicesCount,
                engagements: report.Budget.EngagementsCount,
              }),
            )}

            {renderGroupRow(
              "parcels",
              t("workspace.transfer.groups.parcels"),
              report.Parcels.ParcelsCount + report.Parcels.DecreesCount,
              t("workspace.transfer.groups.parcelsDetails", {
                parcels: report.Parcels.ParcelsCount,
                decrees: report.Parcels.DecreesCount,
              }),
            )}

            {renderGroupRow(
              "approvals",
              t("workspace.transfer.groups.approvals"),
              report.Approvals.PendingCount,
            )}

            {renderGroupRow(
              "tasks",
              t("workspace.transfer.groups.tasks"),
              report.Tasks.TasksCount,
            )}
          </div>
        )}

        {!isLoading && report && report.HasItems && (
          <div className={styles.hint}>{t("workspace.transfer.hint")}</div>
        )}

        <div className={styles.buttons}>
          <HollowButton onClick={toggle} text={t("common.cancel")} />
          <Button
            onClick={handleTransfer}
            text={t("workspace.transfer.confirm")}
            disabled={isLoading || isTransferring || !report?.HasItems}
            loading={isTransferring}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default UserWorkItemsTransferModal;
